<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">優惠券活動成效</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleExport">
          <i class="ri-download-line"></i>
          匯出 Excel
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="filter-group">
        <label>開始日期</label>
        <input
          v-model="filters.startDate"
          type="date"
          class="form-control"
        />
      </div>
      <div class="filter-group">
        <label>結束日期</label>
        <input
          v-model="filters.endDate"
          type="date"
          class="form-control"
        />
      </div>
      <button class="btn btn-primary" @click="fetchData">查詢</button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchData">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>優惠券名稱</th>
            <th>發放數量</th>
            <th>使用數量</th>
            <th>未使用數量</th>
            <th>
              <div ref="usageHelpRef" class="usage-rate-header">
                <span>使用率</span>
                <button
                  type="button"
                  class="usage-help-btn"
                  aria-label="查看使用率計算說明"
                  @click.stop="toggleUsageHelp"
                >
                  <i class="ri-information-line"></i>
                </button>
                <div v-if="showUsageHelp" class="usage-help-popover">
                  <div class="usage-help-title">使用率計算方式</div>
                  <div class="usage-help-text">使用率 = 使用數量 / 發放數量 × 100%</div>
                  <div class="usage-help-text">當發放數量為 0 時，使用率顯示為 0%。</div>
                </div>
              </div>
            </th>
            <th>
              <div ref="revenueHelpRef" class="usage-rate-header">
                <span>用券訂單營收（歸因）</span>
                <button
                  type="button"
                  class="usage-help-btn"
                  aria-label="查看用券訂單營收說明"
                  @click.stop="toggleRevenueHelp"
                >
                  <i class="ri-information-line"></i>
                </button>
                <div v-if="showRevenueHelp" class="usage-help-popover revenue-help-popover">
                  <div class="usage-help-title">欄位計算口徑</div>
                  <div class="usage-help-text">以已核銷優惠券關聯訂單金額計算。</div>
                  <div class="usage-help-text">同模板同訂單只計一次，不代表真實增量營收。</div>
                </div>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="campaigns.length === 0">
            <td colspan="6" class="empty-state">沒有找到資料</td>
          </tr>
          <tr v-for="campaign in campaigns" :key="campaign.couponId">
            <td>{{ campaign.couponName || '-' }}</td>
            <td>{{ campaign.totalIssued }}</td>
            <td>{{ campaign.usedCount }}</td>
            <td>{{ campaign.unusedCount }}</td>
            <td>
              <span :class="getUsageRateClass(campaign.usageRate)">
                {{ (campaign.usageRate * 100).toFixed(2) }}%
              </span>
            </td>
            <td>{{ formatCurrency(campaign.revenueFromCoupon) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { reportsService } from '@/api/modules/reports.js';
import { showError, showSuccess } from '@/utils/swal.js';

export default {
  name: 'CampaignPerformance',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const campaigns = ref([]);
    const showUsageHelp = ref(false);
    const usageHelpRef = ref(null);
    const showRevenueHelp = ref(false);
    const revenueHelpRef = ref(null);
    const filters = ref({
      startDate: '',
      endDate: ''
    });

    const fetchData = async () => {
      try {
        loading.value = true;
        error.value = null;
        
        // 過濾空值
        const params = {};
        if (filters.value.startDate && filters.value.startDate.trim() !== '') {
          params.startDate = filters.value.startDate;
        }
        if (filters.value.endDate && filters.value.endDate.trim() !== '') {
          params.endDate = filters.value.endDate;
        }
        if (filters.value.campaignId && filters.value.campaignId.trim() !== '') {
          params.campaignId = filters.value.campaignId;
        }
        
        const response = await reportsService.getCampaignPerformance(params);
        if (response.success) {
          campaigns.value = response.data.campaigns || [];
        } else {
          error.value = response.message || '取得資料失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得資料失敗';
        showError('錯誤', error.value);
      } finally {
        loading.value = false;
      }
    };

    const handleExport = async () => {
      try {
        loading.value = true;
        const params = {
          reportType: 'campaigns',
          format: 'excel',
          ...filters.value
        };
        const blob = await reportsService.exportReport(params);
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `優惠券活動成效_${filters.value.startDate || 'all'}_${filters.value.endDate || 'all'}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess('成功', '報表已匯出');
      } catch (err) {
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出失敗');
      } finally {
        loading.value = false;
      }
    };

    const formatCurrency = (value) => {
      if (!value) return '$0';
      return `$${parseInt(value).toLocaleString()}`;
    };

    const getUsageRateClass = (rate) => {
      if (rate >= 0.5) return 'badge badge-success';
      if (rate >= 0.3) return 'badge badge-warning';
      return 'badge badge-danger';
    };

    const toggleUsageHelp = () => {
      showUsageHelp.value = !showUsageHelp.value;
      if (showUsageHelp.value) {
        showRevenueHelp.value = false;
      }
    };

    const toggleRevenueHelp = () => {
      showRevenueHelp.value = !showRevenueHelp.value;
      if (showRevenueHelp.value) {
        showUsageHelp.value = false;
      }
    };

    const handleDocumentClick = (event) => {
      if (showUsageHelp.value && usageHelpRef.value && !usageHelpRef.value.contains(event.target)) {
        showUsageHelp.value = false;
      }
      if (showRevenueHelp.value && revenueHelpRef.value && !revenueHelpRef.value.contains(event.target)) {
        showRevenueHelp.value = false;
      }
    };

    onMounted(() => {
      fetchData();
      document.addEventListener('click', handleDocumentClick);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleDocumentClick);
    });

    return {
      loading,
      error,
      campaigns,
      showUsageHelp,
      usageHelpRef,
      showRevenueHelp,
      revenueHelpRef,
      filters,
      fetchData,
      handleExport,
      formatCurrency,
      getUsageRateClass,
      toggleUsageHelp,
      toggleRevenueHelp
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.usage-rate-header {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.usage-help-btn {
  border: none;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0;
}

.usage-help-popover {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 270px;
  background: #ffffff;
  border: 1px solid #dbe4f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
  padding: 10px 12px;
  text-align: left;
  z-index: 20;
}

.revenue-help-popover {
  width: 320px;
}

.usage-help-title {
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.usage-help-text {
  font-size: 12px;
  color: #4b5563;
  line-height: 1.5;
}
</style>
